import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchTemplateSchema } from '../../services/demoApi';
import DEMO_CONFIG from '../../services/demoConfig';

/**
 * Variable type → HTML input type mapping.
 * Mirrors the approach from DoqSolv/DynamicForm.js
 */
const typeToInput = {
  text: 'text',
  number: 'number',
  date: 'date',
  email: 'email',
  phone: 'tel',
  mobile: 'tel',
  currency: 'number',
};

/**
 * Group variables by their `group` metadata.
 * Named groups first (alphabetical), "General" last.
 */
const groupVariables = (vars) => {
  const groups = {};
  vars.forEach(v => {
    const g = v.group?.trim() || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push(v);
  });

  const entries = Object.entries(groups);
  entries.sort(([a], [b]) => {
    if (a === 'General') return 1;
    if (b === 'General') return -1;
    return a.localeCompare(b);
  });

  return entries.map(([groupName, gVars]) => ({ groupName, vars: gVars }));
};

const isValidMobile = (val) => /^[6-9]\d{9}$/.test(val);

const Step1CollectInfo = ({ formData, setFormData, onNext, demoState, setDemoState }) => {
  const [loading, setLoading] = useState(true);
  const [templateError, setTemplateError] = useState('');
  const [template, setTemplate] = useState(null);
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false); // Welcome → Form transition
  const hasFetched = useRef(false);

  // ─── Fetch the template schema on mount ─────────────────────
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchSchema = async () => {
      try {
        const result = await fetchTemplateSchema(DEMO_CONFIG.TEMPLATE_ID);
        const tmpl = result.template || result;
        setTemplate(tmpl);

        // Store variables in shared demoState for later steps
        setDemoState(prev => ({
          ...prev,
          templateVariables: tmpl.variables || [],
          templateName: tmpl.name || '',
        }));

        // Pre-fill default values
        const defaults = {};
        (tmpl.variables || []).forEach(v => {
          if (v.default_value && !formData[v.key]) {
            defaults[v.key] = v.uppercase ? v.default_value.toUpperCase() : v.default_value;
          }
        });
        if (Object.keys(defaults).length > 0) {
          setFormData(prev => ({ ...defaults, ...prev }));
        }
      } catch (err) {
        console.error('Failed to fetch template:', err);
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        setTemplateError(detail);
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [formData, setFormData, setDemoState]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleChange = useCallback((key, rawValue, variableDef) => {
    let value = rawValue;

    if (variableDef?.uppercase && typeof value === 'string') {
      value = value.toUpperCase();
    }

    if (variableDef?.max_length && typeof value === 'string' && value.length > variableDef.max_length) {
      value = value.slice(0, variableDef.max_length);
    }

    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  }, [errors, setFormData]);

  const validate = useCallback(() => {
    if (!template?.variables) return true;

    const newErrors = {};
    template.variables.forEach(v => {
      if (v.is_required && !formData[v.key]?.toString().trim()) {
        newErrors[v.key] = `${v.label} is required`;
      }
      if (v.var_type === 'mobile' && formData[v.key] && !isValidMobile(formData[v.key])) {
        newErrors[v.key] = 'Must start with 6-9 and be exactly 10 digits';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [template, formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onNext();
  };

  // ─── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">1</div>
          <span className="loan-step-text">Step 1 of 5 — Loading Template</span>
        </div>
        <div className="loan-card">
          <div className="loan-processing">
            <div className="loan-spinner" />
            <div className="loan-processing__text">Loading Template Fields...</div>
            <div className="loan-processing__subtext">Fetching variable schema from DoqSolv</div>
          </div>
        </div>
      </>
    );
  }

  // ─── Error state ──────────────────────────────────────────
  if (templateError) {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number" style={{ background: 'var(--loan-error)' }}>!</div>
          <span className="loan-step-text">Step 1 of 5 — Template Error</span>
        </div>
        <div className="loan-card">
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            Template Not Found
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            Could not load the document template. Please verify the Template ID in your environment configuration.
          </p>
          <div className="loan-doc-preview" style={{ textAlign: 'center', padding: 16 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--loan-error)', wordBreak: 'break-all' }}>
              {templateError}
            </p>
          </div>
          <div style={{ marginTop: 16 }}>
            <button
              className="loan-btn loan-btn--primary"
              onClick={() => {
                hasFetched.current = false;
                setTemplateError('');
                setLoading(true);
              }}
            >
              ↻ Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Count variables for the welcome screen ────────────────
  const variables = template?.variables || [];
  const requiredCount = variables.filter(v => v.is_required).length;

  // ══════════════════════════════════════════════════════════════
  // PHASE 1: WELCOME SPLASH
  // ══════════════════════════════════════════════════════════════
  if (!showForm) {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">1</div>
          <span className="loan-step-text">Step 1 of 5 — Get Started</span>
        </div>

        <div className="loan-card" style={{ textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          {/* Accent bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 4,
            background: 'var(--loan-accent)',
          }} />

          {/* Brand logo */}
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'var(--loan-accent)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '12px auto 20px', boxShadow: '0 4px 16px rgba(99, 102, 241, 0.25)',
          }}>
            <span style={{ color: '#fff', fontSize: 26, fontWeight: 800, fontFamily: 'var(--loan-font-display)' }}>
              NB
            </span>
          </div>

          <div className="loan-eyebrow">Welcome to NeoBank</div>
          <h1 className="loan-heading">Secure Your Future Capital</h1>
          <p className="loan-subheading" style={{ marginBottom: 24, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
            Apply for a personal loan in minutes. Complete the digital onboarding process
            with secure document generation, e-stamping, and Aadhaar eSign — all powered by
            the <strong style={{ color: 'var(--loan-accent)' }}>Matrica API Suite</strong>.
          </p>

          {/* Onboarding steps overview */}
          <div style={{ textAlign: 'left', marginBottom: 24 }}>
            <p style={{
              fontSize: 11, fontWeight: 600, color: 'var(--loan-text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ width: 12, height: 1, background: 'var(--loan-border)' }} />
              Onboarding Steps
              <span style={{ flex: 1, height: 1, background: 'var(--loan-border)' }} />
            </p>

            {[
              { icon: '📝', title: 'Fill Application', sub: `${variables.length} fields • ${requiredCount} required` },
              { icon: '📄', title: 'Document Generation & e-Stamp', sub: 'Auto-generated via DoqSolv + Legal stamp via DocuStamp' },
              { icon: '🔏', title: 'Aadhaar eSign', sub: 'Digital signature via NSDL gateway' },
              { icon: '✅', title: 'Welcome & Confirmation', sub: 'Account activation email' },
            ].map((item, i) => (
              <div className="loan-info-row" key={i}>
                <div className="loan-info-row__icon" style={{
                  background: 'var(--loan-accent-soft)', color: 'var(--loan-accent)',
                  fontSize: 16, width: 34, height: 34,
                }}>
                  {item.icon}
                </div>
                <div className="loan-info-row__content">
                  <div className="loan-info-row__title">{item.title}</div>
                  <div className="loan-info-row__subtitle">{item.sub}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', fontWeight: 600 }}>
                  {i + 1}/4
                </span>
              </div>
            ))}
          </div>

          {/* Template info */}
          {template?.name && (
            <div style={{
              background: 'var(--loan-bg-card-alt)', borderRadius: 'var(--loan-radius-md)',
              padding: '12px 16px', marginBottom: 24, display: 'flex',
              alignItems: 'center', gap: 10, textAlign: 'left',
            }}>
              <span style={{ fontSize: 18 }}>📋</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--loan-text-primary)' }}>
                  {template.name}
                </div>
                {template.description && (
                  <div style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', marginTop: 2 }}>
                    {template.description}
                  </div>
                )}
              </div>
              {template?.category && (
                <div className="loan-badge loan-badge--accent" style={{ flexShrink: 0 }}>
                  {template.category}
                </div>
              )}
            </div>
          )}

          <button
            className="loan-btn loan-btn--primary"
            onClick={() => setShowForm(true)}
          >
            Get Started →
          </button>

          <div className="loan-trust-note" style={{ marginTop: 16 }}>
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
            </svg>
            Your data is encrypted end-to-end
          </div>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // PHASE 2: CLEAN FORM (no heading/subheading — just fields)
  // ══════════════════════════════════════════════════════════════
  const groups = groupVariables(variables);
  const hasMultipleGroups = groups.length > 1 || groups[0]?.groupName !== 'General';

  const renderField = (v) => {
    const inputType = typeToInput[v.var_type] || 'text';
    const isTextarea = v.var_type === 'textarea';
    const isSelect = v.var_type === 'select';
    const isCurrency = v.var_type === 'currency';
    const isMobile = v.var_type === 'mobile' || v.var_type === 'phone';
    const currentValue = formData[v.key] || '';
    const mobileInvalid = isMobile && currentValue && !isValidMobile(currentValue);
    const hasError = errors[v.key];

    return (
      <div className="loan-form-group" key={v.key}>
        <label className="loan-label" htmlFor={`loan-field-${v.key}`}>
          {v.label}
          {v.is_required && <span style={{ color: 'var(--loan-error)', marginLeft: 2 }}>*</span>}
          {v.uppercase && (
            <span style={{
              marginLeft: 6, fontSize: 9, color: '#d97706',
              background: 'rgba(217, 119, 6, 0.1)', padding: '1px 5px',
              borderRadius: 3, fontWeight: 600
            }}>
              ABC
            </span>
          )}
          {v.max_length && (
            <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--loan-text-tertiary)' }}>
              (max {v.max_length})
            </span>
          )}
        </label>

        {isTextarea ? (
          <textarea
            id={`loan-field-${v.key}`}
            className="loan-input"
            rows={3}
            value={currentValue}
            onChange={e => handleChange(v.key, e.target.value, v)}
            maxLength={v.max_length || undefined}
            required={v.is_required}
            placeholder={v.default_value || ''}
            style={{
              resize: 'vertical', minHeight: 70,
              ...(hasError ? { borderColor: 'var(--loan-error)' } : {}),
            }}
          />
        ) : isSelect ? (
          <select
            id={`loan-field-${v.key}`}
            className="loan-input"
            value={currentValue}
            onChange={e => handleChange(v.key, e.target.value, v)}
            required={v.is_required}
            style={hasError ? { borderColor: 'var(--loan-error)' } : {}}
          >
            <option value="">Select…</option>
            {(v.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : isCurrency ? (
          <div className="loan-input-prefix">
            <div className="loan-input-addon">₹</div>
            <input
              id={`loan-field-${v.key}`}
              className="loan-input"
              type="number"
              value={currentValue}
              onChange={e => handleChange(v.key, e.target.value, v)}
              required={v.is_required}
              placeholder={v.default_value || '0'}
              min="0"
              step="0.01"
              style={hasError ? { borderColor: 'var(--loan-error)' } : {}}
            />
          </div>
        ) : isMobile ? (
          <>
            <div className="loan-input-prefix">
              <div className="loan-input-addon">+91</div>
              <input
                id={`loan-field-${v.key}`}
                className="loan-input"
                type="tel"
                maxLength={10}
                value={currentValue}
                onChange={e => handleChange(v.key, e.target.value.replace(/\D/g, ''), v)}
                required={v.is_required}
                placeholder={v.default_value || '98765 43210'}
                pattern="[6-9][0-9]{9}"
                style={hasError || mobileInvalid ? { borderColor: 'var(--loan-error)' } : {}}
              />
            </div>
            {mobileInvalid && (
              <span style={{ fontSize: 12, color: 'var(--loan-error)', marginTop: 4, display: 'block' }}>
                Must start with 6-9 and be exactly 10 digits
              </span>
            )}
          </>
        ) : (
          <input
            id={`loan-field-${v.key}`}
            className="loan-input"
            type={inputType}
            value={currentValue}
            onChange={e => handleChange(v.key, e.target.value, v)}
            maxLength={v.max_length || undefined}
            required={v.is_required}
            placeholder={v.default_value || ''}
            style={hasError ? { borderColor: 'var(--loan-error)' } : {}}
          />
        )}

        {hasError && (
          <span style={{ fontSize: 12, color: 'var(--loan-error)', marginTop: 4, display: 'block' }}>
            {errors[v.key]}
          </span>
        )}
      </div>
    );
  };

  const allRequiredFilled = variables
    .filter(v => v.is_required)
    .every(v => formData[v.key]?.toString().trim());

  return (
    <>
      <div className="loan-step-label">
        <div className="loan-step-number">1</div>
        <span className="loan-step-text">Step 1 of 5 — Your Information</span>
      </div>

      <div className="loan-card">
        {/* Back to welcome */}
        <button
          className="loan-back-link"
          onClick={() => setShowForm(false)}
          style={{ marginBottom: 16 }}
        >
          ← Back
        </button>

        <form onSubmit={handleSubmit} autoComplete="off">
          {groups.map(({ groupName, vars }) => (
            <div key={groupName} style={{ marginBottom: 20 }}>
              {hasMultipleGroups && (
                <div style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--loan-text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ width: 12, height: 1, background: 'var(--loan-border)' }} />
                  {groupName}
                  <span style={{ flex: 1, height: 1, background: 'var(--loan-border)' }} />
                </div>
              )}

              {vars.map(renderField)}
            </div>
          ))}

          <div style={{ marginTop: 28 }}>
            <button
              type="submit"
              className="loan-btn loan-btn--primary"
              disabled={!allRequiredFilled}
            >
              Proceed to Document Generation →
            </button>
          </div>
        </form>

        <div className="loan-trust-note">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
          </svg>
          Your data is encrypted
        </div>
      </div>
    </>
  );
};

export default Step1CollectInfo;
